import React, { useState, useEffect } from 'react';
import { COLORS, TYPOGRAPHY } from '../../lib/styleGuide';

const ACCENT_GRAY = '#757575';
const ERROR_RED = '#F44336';

const initial = {
  street: '',
  apt: '',
  city: '',
  state: '',
  zip: '',
};

function validateAddress(address) {
  if (!address.street.trim()) return { isValid: false, error: 'Street address is required.' };
  if (!address.city.trim()) return { isValid: false, error: 'City is required.' };
  if (!address.state.trim()) return { isValid: false, error: 'State is required.' };
  if (!address.zip.trim()) return { isValid: false, error: 'ZIP code is required.' };
  if (!/^\d{5}(-\d{4})?$/.test(address.zip.trim())) return { isValid: false, error: 'Enter a valid ZIP code.' };
  return { isValid: true, error: null };
}

export default function ShopLocationStep({ data, onNext, onBack, isFirst, isLast }) {
  const [address, setAddress] = useState({ ...initial, ...(data.shopAddress || {}) });
  const [touched, setTouched] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    setError('');
    const { isValid, error } = validateAddress(address);
    if (!isValid && Object.values(touched).some(Boolean)) setError(error);
    else setError('');
  }, [address, touched]);

  function handleChange(field, value) {
    setAddress(a => ({ ...a, [field]: value }));
    setTouched(t => ({ ...t, [field]: true }));
  }

  function handleNext(e) {
    e.preventDefault();
    setTouched({ street: true, city: true, state: true, zip: true });
    const { isValid, error } = validateAddress(address);
    if (!isValid) {
      setError(error);
      return;
    }
    onNext({ shopAddress: address });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, minHeight: '80vh' }}>
      {/* Header Section */}
      <div>
        <h2 style={{ fontWeight: 700, fontSize: TYPOGRAPHY.ONBOARDING.H2, margin: 0, color: '#222' }}>Confirm Your Shop Location</h2>
        <p style={{ color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, margin: '10px 0 0 0' }}>
          Please confirm your shop address details. This will help customers find you and ensure proper delivery zones.
        </p>
      </div>

      {/* Address Form */}
      <form onSubmit={handleNext} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div>
          <label htmlFor="street" style={{ fontWeight: 600, color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION }}>Street Address</label>
          <input
            id="street"
            name="street"
            value={address.street}
            onChange={e => handleChange('street', e.target.value)}
            required
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid #E5E7EB',
              borderRadius: 8,
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION,
              marginTop: 4
            }}
            autoComplete="address-line1"
            aria-label="Street Address"
          />
        </div>

        <div>
          <label htmlFor="apt" style={{ fontWeight: 600, color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION }}>Apt/Suite (optional)</label>
          <input
            id="apt"
            name="apt"
            value={address.apt}
            onChange={e => handleChange('apt', e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid #E5E7EB',
              borderRadius: 8,
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION,
              marginTop: 4
            }}
            autoComplete="address-line2"
            aria-label="Apt or Suite"
          />
        </div>

        <div>
          <label htmlFor="city" style={{ fontWeight: 600, color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION }}>City</label>
          <input
            id="city"
            name="city"
            value={address.city}
            onChange={e => handleChange('city', e.target.value)}
            required
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid #E5E7EB',
              borderRadius: 8,
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION,
              marginTop: 4
            }}
            autoComplete="address-level2"
            aria-label="City"
          />
        </div>

        <div>
          <label htmlFor="state" style={{ fontWeight: 600, color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION }}>State</label>
          <input
            id="state"
            name="state"
            value={address.state}
            onChange={e => handleChange('state', e.target.value)}
            required
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid #E5E7EB',
              borderRadius: 8,
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION,
              marginTop: 4
            }}
            autoComplete="address-level1"
            aria-label="State"
          />
        </div>

        <div>
          <label htmlFor="zip" style={{ fontWeight: 600, color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION }}>ZIP Code</label>
          <input
            id="zip"
            name="zip"
            value={address.zip}
            onChange={e => handleChange('zip', e.target.value)}
            required
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid #E5E7EB',
              borderRadius: 8,
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION,
              marginTop: 4
            }}
            autoComplete="postal-code"
            aria-label="ZIP Code"
          />
        </div>

        {/* Validation Message */}
        <div style={{ minHeight: 22, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, marginTop: 2 }}>
          {error && <span style={{ color: ERROR_RED }}>{error}</span>}
        </div>

        {/* Navigation Buttons */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          gap: 12, 
          marginTop: 'auto'
        }}>
          {!isFirst && (
            <button
              type="button"
              onClick={onBack}
              style={{
                background: '#fff',
                color: '#9CA3AF',
                border: 'none',
                borderRadius: 10,
                padding: '14px 32px',
                fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
                fontWeight: 600,
                cursor: 'pointer',
                minHeight: 44,
                minWidth: 120,
                transition: 'all 0.2s',
              }}
              aria-label="Back"
            >
              Back
            </button>
          )}
          <button
            type="submit"
            disabled={!validateAddress(address).isValid}
            style={{
              background: validateAddress(address).isValid ? COLORS.PRIMARY_GRADIENT : '#E0CFC2',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '14px 32px',
              fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
              fontWeight: 600,
              cursor: validateAddress(address).isValid ? 'pointer' : 'not-allowed',
              minHeight: 44,
              minWidth: 120,
              boxShadow: '0 1px 4px #0001',
              transition: 'background 0.2s',
            }}
            aria-label="Next"
          >
            Next
          </button>
        </div>
      </form>
    </div>
  );
} 